import React from "react";
import {
  Box,
  Typography,
  Divider,
  Avatar,
  Stack,
  Paper,
  Card,
  CardContent,
} from "@mui/material";
import {
  AccessTime as TimeIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  SwapHoriz as SwapIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import moment from "moment";
import useHistory from "../../hooks/useHistory";
import useWaterUnit from "../../hooks/useWaterUnit";

function GetFormattedChange(title, value, waterLabel) {
  // Format according to data type
  if (title === "active") {
    return { type: "status", value: value };
  } else if (title === "name") {
    return { type: "text", label: "Name", value: value };
  } else if (title === "proteins" || title === "carbs" || title === "fats") {
    return {
      type: "numeric",
      label: title.charAt(0).toUpperCase() + title.slice(1),
      value: Math.round(value) + "g",
    };
  } else if (title === "water") {
    return { type: "numeric", label: "Water", value: waterLabel ? waterLabel(value) : `${value}ml` };
  } else if (title === "kcals") {
    return { type: "numeric", label: "Calories", value: value };
  } else if (title === "session" || title === "sessions") {
    // Handle both singular and plural "sessions" consistently
    return { type: "numeric", label: "Sessions", value: value };
  } else if (title === "duration") {
    // Format duration consistently
    let formattedValue = value;
    if (typeof value === "string" && !value.toLowerCase().includes("min")) {
      formattedValue = value + " minutes";
    } else if (typeof value === "number") {
      formattedValue = value + " minutes";
    }
    return { type: "text", label: "Duration", value: formattedValue };
  } else if (title === "intensity") {
    return { type: "text", label: "Intensity", value: value };
  } else if (title === "targetHR" || title === "hr" || title === "target HR") {
    // Standardize HR field names
    return { type: "text", label: "Target HR", value: value };
  }

  // Default formatting
  return {
    type: "text",
    label: title.charAt(0).toUpperCase() + title.slice(1),
    value: value,
  };
}

function HistoryChangeItem({ change, client }) {
  const { waterLabel } = useWaterUnit();
  const { differences } = change;
  const differenceKeys = Object.keys(differences);

  // Check if this is a status change (active/inactive)
  const hasStatusChange = differenceKeys.includes("active");

  if (hasStatusChange) {
    const isActivated = differences.active === true;
    return (
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 0.5 }}>
        <Avatar
          sx={{
            bgcolor: isActivated ? "success.light" : "error.light",
            width: 24,
            height: 24,
            mr: 1,
          }}
        >
          {isActivated ? (
            <CheckIcon fontSize="small" />
          ) : (
            <ClearIcon fontSize="small" />
          )}
        </Avatar>
        <Box>
          <Typography variant="body2">
            <strong>{change.name}</strong> was{" "}
            {isActivated ? "activated" : "deactivated"}
          </Typography>
          {differenceKeys.length > 1 && (
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              sx={{ mt: 0.25 }}
            >
              {differenceKeys
                .filter((key) => key !== "active")
                .map((key) => {
                  const { label, value } = GetFormattedChange(
                    key,
                    differences[key],
                    waterLabel,
                  );
                  return (
                    <Typography
                      key={key}
                      variant="caption"
                      color="text.secondary"
                    >
                      {label}: {value}
                    </Typography>
                  );
                })}
            </Stack>
          )}
        </Box>
      </Box>
    );
  }

  // For other types of changes (not status changes)
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 0.5 }}>
      <Avatar
        sx={{
          bgcolor: "primary.light",
          width: 24,
          height: 24,
          mr: 1,
        }}
      >
        <EditIcon fontSize="small" />
      </Avatar>
      <Box sx={{ width: "100%" }}>
        <Typography variant="body2" sx={{ mb: 0.25 }}>
          <strong>{change.name}</strong> was modified
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 16px",
            pl: 0.5,
            mt: 0.5,
          }}
        >
          {differenceKeys.map((key) => {
            const { label, value: prevValue } = GetFormattedChange(
              key,
              differences[key],
              waterLabel,
            );

            // Try to find current value if possible based on the goal ID
            let currentValue = null;
            let formattedCurrentValue = null;
            if (client) {
              // Determine if this is a cardio goal or nutrition goal
              const isCardioGoal =
                change.name.toLowerCase().includes("cardio") ||
                [
                  "jump rope",
                  "bike ride",
                  "run",
                  "swim",
                  "walk",
                  "hiit",
                  "cardio",
                ].some((term) => change.name.toLowerCase().includes(term));

              // Find the appropriate goals collection
              const goalType = isCardioGoal ? "cardioGoals" : "nutritionGoals";
              const goals = client[goalType] || [];

              // Try to find the goal by id or name if id isn't available
              let goal = goals.find((g) => g.id === change.id);
              if (!goal) {
                goal = goals.find((g) => g.name === change.name);
              }

              if (goal) {
                // Handle different field name variations
                let fieldKey = key;

                // Map common field name variations
                if (
                  key === "targetHR" &&
                  Object.prototype.hasOwnProperty.call(goal, "hr")
                ) {
                  fieldKey = "hr";
                } else if (
                  key === "hr" &&
                  Object.prototype.hasOwnProperty.call(goal, "targetHR")
                ) {
                  fieldKey = "targetHR";
                } else if (
                  key === "session" &&
                  Object.prototype.hasOwnProperty.call(goal, "sessions")
                ) {
                  fieldKey = "sessions";
                } else if (
                  key === "sessions" &&
                  Object.prototype.hasOwnProperty.call(goal, "session")
                ) {
                  fieldKey = "session";
                }

                if (goal[fieldKey] !== undefined) {
                  currentValue = goal[fieldKey];

                  // Format current value based on field type
                  const formattedChange = GetFormattedChange(
                    fieldKey,
                    currentValue,
                    waterLabel,
                  );
                  formattedCurrentValue = formattedChange.value;
                }
              }
            }

            return (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  mb: 0.75,
                  mr: 2,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="medium"
                  color="text.secondary"
                >
                  {label}
                </Typography>
                <Typography variant="body2">
                  {prevValue}
                  {currentValue !== null && (
                    <>
                      <SwapIcon
                        sx={{
                          fontSize: 14,
                          verticalAlign: "middle",
                          mx: 0.5,
                          color: "primary.main",
                        }}
                      />
                      <span style={{ color: "#2e7d32", fontWeight: 500 }}>
                        {formattedCurrentValue}
                      </span>
                    </>
                  )}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function HistoryEvent({ historyItem, client }) {
  const date = historyItem.changedOn.toDate();
  const formattedDate = moment(date).format("MMM D, YYYY");
  const formattedTime = moment(date).format("h:mm A");

  // Check if there was a goal switch (one activated, one deactivated)
  const activatedGoal = historyItem.changes.find(
    (c) => c.differences.active === true,
  );
  const deactivatedGoal = historyItem.changes.find(
    (c) => c.differences.active === false,
  );
  const isGoalSwitch = activatedGoal && deactivatedGoal;

  if (isGoalSwitch) {
    return (
      <Card variant="outlined" sx={{ mb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            pt: 1,
            px: 2,
            pb: 0.5,
          }}
        >
          <Avatar
            sx={{
              bgcolor: "info.light",
              width: 28,
              height: 28,
              mr: 1.5,
            }}
          >
            <SwapIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body1" fontWeight={500}>
              Goal Switch
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TimeIcon
                fontSize="small"
                sx={{ mr: 0.5, fontSize: 14, color: "text.secondary" }}
              />
              <Typography variant="caption" color="text.secondary">
                {formattedDate} at {formattedTime}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider />
        <CardContent sx={{ py: 1, px: 2 }}>
          <Typography variant="body2">
            Goal switched from <strong>{deactivatedGoal.name}</strong> to{" "}
            <strong>{activatedGoal.name}</strong>
          </Typography>

          {activatedGoal.differences &&
            Object.keys(activatedGoal.differences).length > 1 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "2px 12px",
                  mt: 0.5,
                }}
              >
                {Object.keys(activatedGoal.differences)
                  .filter((key) => key !== "active")
                  .map((key) => {
                    const { label, value } = GetFormattedChange(
                      key,
                      activatedGoal.differences[key],
                      waterLabel,
                    );
                    return (
                      <Typography key={key} variant="caption">
                        <span style={{ color: "rgba(0,0,0,0.6)" }}>
                          {label}:
                        </span>{" "}
                        {value}
                      </Typography>
                    );
                  })}
              </Box>
            )}
        </CardContent>
      </Card>
    );
  }

  // Regular history item display
  return (
    <Card variant="outlined" sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", p: 1, pl: 2 }}>
        <TimeIcon
          fontSize="small"
          sx={{ mr: 0.5, fontSize: 14, color: "text.secondary" }}
        />
        <Typography variant="caption" color="text.secondary">
          {formattedDate} at {formattedTime}
        </Typography>
      </Box>
      <Divider />
      <CardContent sx={{ py: 1, px: 2 }}>
        {historyItem.changes.map((change) => (
          <HistoryChangeItem key={change.id} change={change} client={client} />
        ))}
      </CardContent>
    </Card>
  );
}

function RecentHistory({ client, historyCollection, limit = 10 }) {
  const { history } = useHistory({
    uid: client.uid,
    historyCollection: historyCollection,
    limit: limit,
  });

  if (history.length === 0) {
    return (
      <Paper
        sx={{
          p: 2,
          textAlign: "center",
          bgcolor: "background.paper",
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No history data available
        </Typography>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        maxHeight: 350,
        overflowY: "auto",
        px: 0.5,
      }}
    >
      <Stack spacing={0.75}>
        {history.map((historyItem) => (
          <HistoryEvent
            key={historyItem.id}
            historyItem={historyItem}
            client={client}
          />
        ))}
      </Stack>
    </Box>
  );
}

export default RecentHistory;
